import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react'

// A tiny keyed fetch cache with in-flight dedupe and stale-while-revalidate.
// Every hook is a bare `fetch` in `useEffect` today, so the same endpoint is
// requested once per call site and again on every navigation. Routing reads
// through this shared, module-level cache instead: callers passing the same
// `key` share one request and one result, and a revisited screen renders from
// memory while it revalidates in the background.

interface Snapshot<T = unknown> {
  data?: T
  error?: unknown
  // 0 means "never loaded" — used both to gate the initial fetch and to derive
  // the loading flag. Bumped to Date.now() on every successful load.
  updatedAt: number
}

interface Entry {
  snap: Snapshot
  promise?: Promise<void>
  subs: Set<() => void>
  // The last fetcher seen for this key, so an entry can be refreshed by
  // something that does not hold the caller's closure — an invalidation after a
  // mutation, or the tab regaining focus.
  fetcher?: () => Promise<unknown>
  // When something declared this entry out of date. A load older than this is
  // refetched even if it is still inside its TTL.
  invalidatedAt: number
}

// Shared idle snapshot. Never mutated — every update replaces `entry.snap` with
// a fresh object so useSyncExternalStore detects the change by reference.
const EMPTY: Snapshot = { updatedAt: 0 }
const DEFAULT_TTL_MS = 30_000

const cache = new Map<string, Entry>()

function getEntry(key: string): Entry {
  let entry = cache.get(key)
  if (!entry) {
    entry = { snap: EMPTY, subs: new Set(), invalidatedAt: 0 }
    cache.set(key, entry)
  }
  return entry
}

function notify(entry: Entry) {
  for (const sub of entry.subs) sub()
}

// Fetch `key` unless a request is already in flight (that's the dedupe). The
// snapshot is replaced, not mutated, so subscribers re-render.
function revalidate(key: string, fetcher: () => Promise<unknown>): Promise<void> {
  const entry = getEntry(key)
  if (entry.promise) return entry.promise
  entry.promise = fetcher().then(
    (data) => {
      entry.snap = { data, updatedAt: Date.now() }
      entry.promise = undefined
      notify(entry)
    },
    (error) => {
      // Keep any previously loaded data; just record the error.
      entry.snap = { ...entry.snap, error }
      entry.promise = undefined
      notify(entry)
    },
  )
  return entry.promise
}

export interface CachedFetchResult<T> {
  data: T | undefined
  error: unknown
  loading: boolean
  refresh: () => Promise<void>
}

/**
 * Keyed fetch with in-flight dedupe and stale-while-revalidate, backed by a
 * module-level cache. Every caller passing the same `key` shares one request
 * and one cached result; `key === null` is the idle state (nothing fetched).
 *
 * The `key` MUST encode every input the `fetcher` depends on (gym id, period,
 * gender, …). The fetcher is read from a ref and never triggers a refetch on
 * its own, so a stale closure hidden behind an unchanged key would go unnoticed.
 */
export function useCachedFetch<T>(
  key: string | null,
  fetcher: () => Promise<T>,
  options?: { ttlMs?: number },
): CachedFetchResult<T> {
  const ttlMs = options?.ttlMs ?? DEFAULT_TTL_MS

  // Keep the latest fetcher without making it a dependency of the fetch effect —
  // callers pass a new inline closure every render, and we don't want that alone
  // to trigger a refetch (the key is the source of truth for what to fetch).
  // Synced in an effect (not during render) so it runs before the fetch effect
  // in the same commit.
  const fetcherRef = useRef(fetcher)
  useEffect(() => {
    fetcherRef.current = fetcher
  })

  const subscribe = useCallback(
    (cb: () => void) => {
      if (!key) return () => {}
      const entry = getEntry(key)
      entry.subs.add(cb)
      return () => {
        entry.subs.delete(cb)
      }
    },
    [key],
  )

  const getSnapshot = useCallback(() => (key ? (cache.get(key)?.snap ?? EMPTY) : EMPTY), [key])
  const snap = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)

  useEffect(() => {
    if (!key) return
    const entry = getEntry(key)
    entry.fetcher = () => fetcherRef.current()
    const isStale = Date.now() - entry.snap.updatedAt > ttlMs
    const wasInvalidated = entry.snap.updatedAt < entry.invalidatedAt
    if (entry.snap.updatedAt === 0 || isStale || wasInvalidated) {
      void revalidate(key, entry.fetcher)
    }
  }, [key, ttlMs])

  const refresh = useCallback(() => {
    if (!key) return Promise.resolve()
    return revalidate(key, () => fetcherRef.current())
  }, [key])

  return {
    data: snap.data as T | undefined,
    error: snap.error,
    loading: !!key && snap.updatedAt === 0 && snap.error === undefined,
    refresh,
  }
}

/**
 * Patch an already-loaded entry in place, without a network round trip. Use it
 * when a mutation's response already tells you the new state (an RSVP count, a
 * toggled flag) so every subscriber — including a sidebar badge rendered from
 * the same key — updates immediately.
 *
 * No-ops when the key was never loaded: writing data into an empty entry would
 * satisfy the `updatedAt === 0` gate in the fetch effect and suppress the
 * initial load forever. `updatedAt` is deliberately preserved, so an optimistic
 * patch never counts as a fresh server read and never postpones revalidation.
 *
 * The updater MUST return new objects/arrays — subscribers compare snapshots by
 * reference. A revalidation already in flight will overwrite the patch when it
 * lands; patch after the mutation response resolves so the server is the one
 * source of truth.
 */
export function mutateCachedFetch<T>(key: string, updater: (prev: T) => T): void {
  const entry = cache.get(key)
  if (!entry || entry.snap.updatedAt === 0) return
  entry.snap = { ...entry.snap, data: updater(entry.snap.data as T) }
  notify(entry)
}

/**
 * Declare cached data out of date after a mutation changed it.
 *
 * Pass a key prefix (`'ascent-stats:'`) or a predicate. Anything currently on
 * screen refetches immediately and keeps showing what it has until the new data
 * lands; anything not mounted is marked so it refetches the next time it is.
 *
 * This is what stale-while-revalidate cannot do on its own: the cache has no
 * idea that logging an ascent changed your totals and your standing, so without
 * being told it will happily serve the old numbers for the rest of the TTL.
 * Every mutation is responsible for naming what it invalidated.
 */
export function invalidateCachedFetch(match: string | ((key: string) => boolean)): void {
  const matches = typeof match === 'string' ? (key: string) => key.startsWith(match) : match

  for (const [key, entry] of cache) {
    if (!matches(key)) continue
    entry.invalidatedAt = Date.now()
    // Only refetch what someone is looking at. An entry with no subscribers is
    // refreshed by its own mount effect when a screen next needs it.
    if (entry.subs.size > 0 && entry.fetcher) void revalidate(key, entry.fetcher)
  }
}

/**
 * Refetch everything on screen when the app comes back to the foreground.
 *
 * Explicit invalidation only covers changes this client made. A competition
 * starting, the desk checking you in, another climber overtaking you in the
 * standings — none of that passes through here, and a member who switches away
 * and back is exactly the person expecting to see it.
 */
/**
 * A return to the app leaves alone anything loaded more recently than this.
 * Every database call is a line in Supabase's log quota, and refetching data
 * that is seconds old tells the member nothing new. Invalidated entries are
 * refetched whatever their age.
 */
export const FOREGROUND_MIN_AGE_MS = 30_000

// Whether the page has been hidden since the last refresh on return. A window
// regaining focus without ever being hidden is a desk computer clicking between
// the browser and another app: the page was on screen the whole time.
let hiddenSinceReturn = false

if (typeof document !== 'undefined') {
  const revalidateVisible = (event: Event) => {
    if (document.visibilityState !== 'visible') {
      hiddenSinceReturn = true
      return
    }
    if (event.type === 'focus' && !hiddenSinceReturn) return
    hiddenSinceReturn = false
    const now = Date.now()
    for (const [key, entry] of cache) {
      if (entry.subs.size === 0 || !entry.fetcher) continue
      const invalidated = entry.snap.updatedAt < entry.invalidatedAt
      if (!invalidated && now - entry.snap.updatedAt < FOREGROUND_MIN_AGE_MS) continue
      void revalidate(key, entry.fetcher)
    }
  }
  document.addEventListener('visibilitychange', revalidateVisible)
  window.addEventListener('focus', revalidateVisible)
}

/** Drop every cached entry. For tests and hot-module reload only. */
export function clearCachedFetch() {
  cache.clear()
  hiddenSinceReturn = false
}
